import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, Users, Briefcase, TrendingUp, DollarSign,
  AlertTriangle, Clock, FileText, Mail, ArrowRight,
  CheckCircle2, Activity, Bell, MessageSquare, Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CRMDashboard({ onSectionChange }) {
  const [stats, setStats] = useState(null);
  const [crmStats, setCrmStats] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [draftCounts, setDraftCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, crmRes, inboxRes, draftsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/crm/dashboard-stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/comm/inbox`, { headers }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/ai-success/queue?status=pending`, { headers }).catch(() => ({ data: { drafts: [], counts: {} } })),
      ]);
      setStats(dashRes.data?.stats || {});
      setCrmStats(crmRes.data);
      setInbox(inboxRes.data?.items || []);
      setDraftCounts(draftsRes.data?.counts || {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="crm-dashboard-loading">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
    </div>
  );

  const s = stats || {};
  const crm = crmStats || {};
  const pendingDrafts = draftCounts?.pending || 0;

  const kpis = [
    { label: "Borrowers", value: s.borrowers || 0, icon: Users, color: "text-blue-600", bg: "bg-blue-50 border-blue-100" },
    { label: "Lenders", value: s.lenders_investors || 0, icon: Briefcase, color: "text-cyan-600", bg: "bg-cyan-50 border-cyan-100" },
    { label: "Investors", value: s.investors || 0, icon: TrendingUp, color: "text-amber-600", bg: "bg-amber-50 border-amber-100" },
    { label: "Active Deals", value: s.active_deals || 0, icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-100" },
    { label: "Engagement Rate", value: `${crm.lender_engagement_rate || 0}%`, icon: Activity, color: "text-purple-600", bg: "bg-purple-50 border-purple-100" },
    { label: "AI Drafts Pending", value: pendingDrafts, icon: Mail, color: "text-orange-600", bg: "bg-orange-50 border-orange-100" },
  ];

  // Build attention items
  const attentionItems = [];
  if ((crm.incomplete_applications || 0) > 0) attentionItems.push({ label: `${crm.incomplete_applications} Incomplete Apps`, icon: FileText, color: "text-amber-600 bg-amber-50 border-amber-200", section: "borrowers" });
  if ((crm.stale_deals || 0) > 0) attentionItems.push({ label: `${crm.stale_deals} Stale Deals`, icon: Clock, color: "text-red-600 bg-red-50 border-red-200", section: "borrowers" });
  if ((crm.needs_docs || 0) > 0) attentionItems.push({ label: `${crm.needs_docs} Need Documents`, icon: FileText, color: "text-blue-600 bg-blue-50 border-blue-200", section: "borrowers" });
  if (pendingDrafts > 0) attentionItems.push({ label: `${pendingDrafts} AI Drafts to Review`, icon: Mail, color: "text-orange-600 bg-orange-50 border-orange-200", section: "email_outreach" });

  const ICON_MAP = { request: MessageSquare, thread: Mail, notification: Bell };
  const COLOR_MAP = { request: "bg-blue-400", thread: "bg-emerald-400", notification: "bg-amber-400" };

  return (
    <div className="space-y-5" data-testid="crm-dashboard">
      <div>
        <h2 className="text-lg font-bold text-[#0A0A0A]">CRM Dashboard</h2>
        <p className="text-xs text-[#888] mt-0.5">Your success team's command center</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((kpi, i) => (
          <motion.div key={kpi.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className={`rounded-xl border ${kpi.bg} p-3.5`} data-testid={`crm-kpi-${i}`}>
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon className={`h-3.5 w-3.5 ${kpi.color}`} />
              <span className="text-[9px] uppercase tracking-wider text-[#999] font-semibold">{kpi.label}</span>
            </div>
            <div className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</div>
          </motion.div>
        ))}
      </div>

      {/* Needs Attention */}
      {attentionItems.length > 0 && (
        <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-4" data-testid="crm-attention">
          <h3 className="text-xs font-bold uppercase tracking-wider text-orange-700/80 mb-3 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Needs Attention
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {attentionItems.map((item, i) => (
              <div key={i} className={`flex items-center gap-3 rounded-xl border bg-white p-3 cursor-pointer hover:shadow-sm transition-all ${item.color.split(" ").slice(1).join(" ")}`}
                onClick={() => onSectionChange?.(item.section)} data-testid={`crm-attn-${i}`}>
                <item.icon className={`h-5 w-5 ${item.color.split(" ")[0]} shrink-0`} />
                <span className="text-sm font-semibold text-[#333]">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Activity Feed (Inbox) */}
        <div className="lg:col-span-2 rounded-xl border border-[#e8e0d0] bg-white p-5" data-testid="crm-activity-feed">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#999] mb-4 flex items-center gap-2">
            <Activity className="h-4 w-4 text-emerald-600" /> Activity Feed
          </h3>
          {inbox.length === 0 ? (
            <div className="text-center py-10 text-xs text-[#999]">No recent activity</div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {inbox.slice(0, 20).map((item, i) => {
                const ItemIcon = ICON_MAP[item.type] || Bell;
                const dotColor = COLOR_MAP[item.type] || "bg-gray-400";
                return (
                  <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                    className="flex items-start gap-3 py-2.5 px-3 rounded-xl hover:bg-[#faf7f2] transition-colors cursor-pointer border border-transparent hover:border-[#e8e0d0]"
                    data-testid={`crm-feed-${i}`}>
                    <div className={`w-2 h-2 rounded-full ${dotColor} mt-2 shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[#333] line-clamp-2">
                        {item.title || item.subject || item.description || "Activity"}
                      </div>
                      <div className="text-[10px] text-[#999] mt-0.5 flex items-center gap-2">
                        <span>{item.type}</span>
                        {item.created_at && <span>{new Date(item.created_at).toLocaleString()}</span>}
                      </div>
                    </div>
                    {!item.read && <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 shrink-0" />}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="space-y-4">
          {/* AI Agent Card */}
          <div className="rounded-xl border border-purple-200 bg-purple-50/30 p-4" data-testid="crm-ai-agent">
            <h3 className="text-xs font-bold uppercase tracking-wider text-purple-700 mb-3 flex items-center gap-2">
              <Zap className="h-4 w-4 text-purple-500" /> AI Success Agent
            </h3>
            <p className="text-[10px] text-[#888] mb-3">Scan for engagement issues and auto-generate personalized follow-up emails.</p>
            <button onClick={async () => {
              try {
                const res = await axios.post(`${API}/ai-success/scan`, {}, { headers });
                alert(`Scan complete: ${res.data.drafts_created} drafts created. Review them in Email & Outreach.`);
                fetchData();
              } catch (err) { console.error(err); alert("Scan failed"); }
            }}
              className="w-full px-3 py-2.5 rounded-xl bg-purple-600 text-white text-xs font-semibold hover:bg-purple-500 transition-colors flex items-center justify-center gap-2"
              data-testid="crm-ai-scan-btn">
              <Zap className="h-3.5 w-3.5" /> Run AI Scan
            </button>
            {pendingDrafts > 0 && (
              <button onClick={() => onSectionChange?.("email_outreach")}
                className="w-full mt-2 px-3 py-2 rounded-xl border border-purple-300 text-purple-700 text-xs font-semibold hover:bg-purple-100 transition-colors"
                data-testid="crm-view-drafts-btn">
                Review {pendingDrafts} Pending Drafts
              </button>
            )}
          </div>

          {/* Module Links */}
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#999] mb-3">Quick Navigation</h3>
            <div className="space-y-2">
              {[
                { id: "borrowers", label: "Borrowers", icon: Users, color: "text-blue-600", stat: `${s.borrowers || 0}` },
                { id: "lenders", label: "Lenders", icon: Briefcase, color: "text-cyan-600", stat: `${s.lenders_investors || 0}` },
                { id: "investors", label: "Investors", icon: TrendingUp, color: "text-amber-600", stat: `${s.investors || 0}` },
                { id: "email_outreach", label: "Email & Outreach", icon: Mail, color: "text-purple-600", stat: `${crm.total_emails_sent || 0} sent` },
              ].map(mod => (
                <button key={mod.id} onClick={() => onSectionChange?.(mod.id)}
                  className="w-full flex items-center justify-between p-3 rounded-xl border border-[#e8e0d0] hover:border-emerald-300 bg-[#faf7f2] hover:bg-white transition-all text-left group"
                  data-testid={`crm-nav-${mod.id}`}>
                  <div className="flex items-center gap-2">
                    <mod.icon className={`h-4 w-4 ${mod.color}`} />
                    <span className="text-xs font-semibold text-[#333]">{mod.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#999]">{mod.stat}</span>
                    <ArrowRight className="h-3 w-3 text-[#ccc] group-hover:text-emerald-600 transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
